import { ItemState } from '../ItemState';
import { PermissionType } from './PermissionType';

export interface ContentPermission {
    readonly permissions: Array<PermissionType>;
}
export interface UserPermissionsOnContent {
    //* GENERAL
    readonly canSave?: boolean;
    readonly canEdit?: boolean;
    readonly canRemove?: boolean;
    readonly canPublish?: boolean;
    readonly canUnpublish?: boolean;

    //* INFORMATIONS
    readonly canEditTitle?: boolean;
    readonly canEditExternalReference?: boolean;
    readonly canEditEndDate?: boolean;
    readonly canEditDescription?: boolean;
    readonly canEditCategory?: boolean;
    readonly canEditPdf?: boolean;
    readonly image?: {
        canAdd?: boolean;
        canReplace?: boolean;
        canRemove?: boolean;
    };

    readonly video?: {
        //!ONLY IN VIDEO MODULE
        canAdd?: boolean;
        canReplace?: boolean;
        canRemove?: boolean;
    };
    readonly previewSecondsOffset?: boolean; //!ONLY IN VIDEO MODULE

    //* QUESTIONS : AUDITS
    readonly canImportQuestions?: boolean;
    readonly canExportQuestions?: boolean;

    //* NOTIFICATIONS
    readonly canEditAudiences?: boolean;

    //* NOTIFICATIONS
    readonly canReadNotifications?: boolean;
    readonly canEditNotificationsType?: boolean;
    readonly canSendManualNotification?: boolean;

    //* RESULTS
    readonly canReadResults?: boolean;
    readonly canExportResults?: boolean;
    readonly canReadReplies?: boolean;
    readonly canExportReplies?: boolean;
}

export class UserPermissions {
    public static has(permission: PermissionType, contentPermission?: ContentPermission) {
        return contentPermission
            ? (contentPermission.permissions || []).includes(permission)
            : false;
    }

    public static toUserPermissionsOnContent(
        contentState: ItemState,
        contentPermission?: ContentPermission
    ): UserPermissionsOnContent {
        const canEdit = UserPermissions.canEdit(contentState, contentPermission);
        const isFinished =
            contentState === ItemState.Draft &&
            UserPermissions.has(PermissionType.ContentEditPublished, contentPermission);
        return {
            //* GENERAL
            canRemove:
                contentState === ItemState.Draft &&
                UserPermissions.has(PermissionType.ContentEditDraft, contentPermission),
            canSave: canEdit,
            canEdit: canEdit,
            canEditTitle: canEdit,
            canEditExternalReference: canEdit,
            canEditEndDate: canEdit || isFinished,
            canEditDescription: canEdit,
            canEditCategory: canEdit,
            canEditPdf: canEdit || isFinished,
            image: {
                canAdd: canEdit,
                canReplace: canEdit,
                canRemove: canEdit,
            },
            video: {
                canAdd:
                    contentState === ItemState.Draft &&
                    UserPermissions.has(PermissionType.ContentEditDraft, contentPermission),
                canReplace: canEdit,
                canRemove: canEdit,
            },
            previewSecondsOffset: canEdit,
            canPublish:
                (contentState === ItemState.Draft &&
                    UserPermissions.has(PermissionType.ContentPublish, contentPermission)) ||
                (contentState === ItemState.Unpublished &&
                    UserPermissions.has(PermissionType.ContentEditPublished, contentPermission)),
            canUnpublish:
                contentState === ItemState.Published &&
                UserPermissions.has(PermissionType.ContentUnpublish, contentPermission),
            //* QUESTIONS
            canImportQuestions: canEdit,
            canExportQuestions: canEdit,
            //* AUDIENCES
            canEditAudiences: canEdit,
            //* NOTIFICATIONS
            canReadNotifications: UserPermissions.has(
                PermissionType.ContentNotifications,
                contentPermission
            ),
            canEditNotificationsType:
                contentState === ItemState.Draft &&
                UserPermissions.has(PermissionType.ContentEditDraft, contentPermission),
            canSendManualNotification:
                contentState === ItemState.Published &&
                UserPermissions.has(PermissionType.ContentNotifications, contentPermission),
            //* RESULTS
            canReadResults:
                (contentState === ItemState.Published &&
                    UserPermissions.has(PermissionType.ContentReadResults, contentPermission)) ||
                (contentState === ItemState.Unpublished &&
                    UserPermissions.has(PermissionType.ContentReadResults, contentPermission)),
            canExportResults:
                (contentState === ItemState.Published &&
                    UserPermissions.has(PermissionType.ContentReadResults, contentPermission)) ||
                (contentState === ItemState.Unpublished &&
                    UserPermissions.has(PermissionType.ContentReadResults, contentPermission)) ||
                (contentState === ItemState.Finished &&
                    UserPermissions.has(PermissionType.ContentReadResults, contentPermission)),
            canReadReplies:
                (contentState === ItemState.Published &&
                    UserPermissions.has(PermissionType.ContentReadResults, contentPermission)) ||
                (contentState === ItemState.Unpublished &&
                    UserPermissions.has(PermissionType.ContentReadResults, contentPermission)),
            canExportReplies:
                (contentState === ItemState.Published &&
                    UserPermissions.has(PermissionType.ContentReadResults, contentPermission)) ||
                (contentState === ItemState.Unpublished &&
                    UserPermissions.has(PermissionType.ContentReadResults, contentPermission)) ||
                (contentState === ItemState.Finished &&
                    UserPermissions.has(PermissionType.ContentReadResults, contentPermission)),
        };
    }

    public static canEdit(contentState: ItemState, contentPermission?: ContentPermission): boolean {
        return (
            (contentState === ItemState.Draft &&
                UserPermissions.has(PermissionType.ContentEditDraft, contentPermission)) ||
            ((contentState === ItemState.Published || contentState === ItemState.Finished) &&
                UserPermissions.has(PermissionType.ContentEditPublished, contentPermission)) ||
            (contentState === ItemState.Unpublished &&
                UserPermissions.has(PermissionType.ContentEditPublished, contentPermission))
        );
    }

    public static toDefaultPermissions = (): UserPermissionsOnContent => {
        const rawPermissions: UserPermissionsOnContent = UserPermissions.toUserPermissionsOnContent(
            ItemState.Draft
        );
        const permissions: { [field: string]: boolean } = {};
        Object.keys(rawPermissions).forEach((key) => (permissions[key] = true));
        return permissions as UserPermissionsOnContent;
    };
}
